// ===========================================
// Secret Modal Component (formerly EnvVariableModal)
// ===========================================
// Modal for adding/editing keys & secrets in the Vault

import React, { useState, useEffect } from 'react';
import { useProjects } from '../context/ProjectContext';
import { X, Loader, Plus, Key, Save, Link, FileText } from 'lucide-react';
import './Modal.scss';

// Environment options for secret scope
const environmentOptions = [
  { value: 'all', label: 'All Environments' },
  { value: 'development', label: 'Development' },
  { value: 'staging', label: 'Staging' },
  { value: 'production', label: 'Production' }
];

const EnvVariableModal = ({ projectId, envVar = null, onClose, onSaved }) => {
  const { addEnvVariable, updateEnvVariable } = useProjects();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state - includes websiteUrl and notes fields
  const [formData, setFormData] = useState({
    key: '',      // Secret name - no validation, user can use any format
    value: '',    // The secret/key value
    environment: 'all',
    isSecret: true,
    description: '',
    websiteUrl: '',  // Link to service dashboard
    notes: ''        // Additional context
  });

  // Initialize form with secret data if editing
  useEffect(() => {
    if (envVar) {
      setFormData({
        key: envVar.key || '',
        value: '', // Don't pre-fill value for security
        environment: envVar.environment || 'all',
        isSecret: envVar.isSecret !== false, // Default to true
        description: envVar.description || '',
        websiteUrl: envVar.websiteUrl || '',
        notes: envVar.notes || ''
      });
    }
  }, [envVar]);

  // Handle input change - accepts any user input without formatting
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  // Check if we're editing an existing secret
  const isEditing = !!envVar;

  // Handle form submit - supports both add and edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate secret name is provided
    if (!formData.key.trim()) {
      setError('Secret name is required');
      return;
    }
    
    // Value required for new secrets, optional for edits (keeps existing if not provided)
    if (!isEditing && !formData.value.trim()) {
      setError('Secret value is required');
      return;
    }
    
    setLoading(true);
    
    // Build env data with all fields including websiteUrl and notes
    const envData = {
      key: formData.key,
      environment: formData.environment,
      isSecret: formData.isSecret,
      description: formData.description || undefined,
      websiteUrl: formData.websiteUrl || undefined,
      notes: formData.notes || undefined
    };
    
    // Only include value if provided (for edits, empty means keep existing)
    if (formData.value.trim()) {
      envData.value = formData.value;
    }
    
    let result;
    if (isEditing) {
      // Update existing env variable
      result = await updateEnvVariable(projectId, envVar._id, envData);
    } else {
      // Add new env variable
      result = await addEnvVariable(projectId, envData);
    }
    
    if (result.success) {
      onSaved(result.data);
      onClose();
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal__header">
          <h2>
            <Key size={20} />
            {isEditing ? 'Edit Secret' : 'Add Secret'}
          </h2>
          <button className="modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form className="modal__form" onSubmit={handleSubmit}>
          {error && (
            <div className="modal__error">{error}</div>
          )}

          {/* Secret Name - accepts any user input, no formatting validation */}
          <div className="modal__field">
            <label htmlFor="key">Secret Name *</label>
            <input
              id="key"
              name="key"
              type="text"
              placeholder="My API Key"
              value={formData.key}
              onChange={handleChange}
              disabled={loading || isEditing}  // Can't change name when editing
              autoFocus={!isEditing}
              className="env-key-input"
            />
            {isEditing && (
              <span className="modal__field-hint">
                Secret name cannot be changed
              </span>
            )}
          </div>

          {/* Secret Value */}
          <div className="modal__field">
            <label htmlFor="value">{isEditing ? 'Secret (leave empty to keep current)' : 'Secret *'}</label>
            <textarea
              id="value"
              name="value"
              placeholder={formData.isSecret ? "••••••••••••••••" : "Enter secret..."}
              value={formData.value}
              onChange={handleChange}
              disabled={loading}
              rows={3}
            />
          </div>

          {/* Environment & Secret */}
          <div className="modal__row">
            <div className="modal__field">
              <label htmlFor="environment">Environment</label>
              <select
                id="environment"
                name="environment"
                value={formData.environment}
                onChange={handleChange}
                disabled={loading}
              >
                {environmentOptions.map(env => (
                  <option key={env.value} value={env.value}>
                    {env.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="modal__field">
              <label>&nbsp;</label>
              <div className="modal__checkbox">
                <input
                  id="isSecret"
                  name="isSecret"
                  type="checkbox"
                  checked={formData.isSecret}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label htmlFor="isSecret">
                  <span className="checkbox-icon">🔒</span>
                  Mark as secret (encrypted)
                </label>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="modal__field">
            <label htmlFor="description">Description</label>
            <input
              id="description"
              name="description"
              type="text"
              placeholder="What is this variable used for?"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          {/* Website URL - Link to service dashboard */}
          <div className="modal__field">
            <label htmlFor="websiteUrl">
              <Link size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Website URL
            </label>
            <input
              id="websiteUrl"
              name="websiteUrl"
              type="url"
              placeholder="https://dashboard.service.com"
              value={formData.websiteUrl}
              onChange={handleChange}
              disabled={loading}
            />
            <span className="modal__field-hint">
              Quick link to the service dashboard or documentation
            </span>
          </div>

          {/* Notes - Additional context */}
          <div className="modal__field">
            <label htmlFor="notes">
              <FileText size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              placeholder="Additional context, rotation schedule, usage instructions..."
              value={formData.notes}
              onChange={handleChange}
              disabled={loading}
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="modal__actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                <>
                  {isEditing ? <Save size={16} /> : <Plus size={16} />}
                  {isEditing ? 'Save Secret' : 'Add Secret'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnvVariableModal;

